import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { injectTranslate } from '@core/i18n-legacy';
import { SelectTeamMembersDialogComponent } from '@core/tellent-members-smart-ui';
import { SelectMembersDialogResult } from '@core/tellent-members-types';
import { PositionsDialogsOpener } from '@core/tellent-positions-contracts';
import {
  ConfirmPositionChangeDialogData,
  ConfirmPositionChangeDialogResult,
  PositionHistoryDialogData,
  PositionHistoryDialogResult,
  ReassignMemberWarningDialogData,
  ReassignMemberWarningDialogResult,
  TellentPosition,
  TellentPositionMember,
  UnassignMemberFromPositionDialogResult,
} from '@core/tellent-positions-types';
import { AssignedSkill, SkillMapping } from '@core/tellent-skills-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { ConfirmPositionChangeDialogComponent } from '../confirm-position-change-dialog/confirm-position-change-dialog.component';
import { DeletePositionDialogComponent } from '../delete-position-dialog/delete-position-dialog.component';
import { PositionDetailsDialogComponent } from '../position-details-dialog/position-details-dialog.component';
import { PositionHistoryDialogComponent } from '../position-history-dialog/position-history-dialog.component';
import { ReassignMemberWarningDialogComponent } from '../reassign-member-warning-dialog/reassign-member-warning-dialog.component';
import { RenamePositionDialogComponent } from '../rename-position-dialog/rename-position-dialog.component';
import { UnassignMemberFromPositionDialogComponent } from '../unassign-member-from-position-dialog/unassign-member-from-position-dialog.component';

@Injectable()
export class PositionsDialogsService implements PositionsDialogsOpener {
  private readonly dialogService = inject(DialogService);
  private t = injectTranslate();

  public openPositionDetails(positionGuid: string): void {
    this.dialogService.openCustom(
      PositionDetailsDialogComponent,
      { width: DialogWidth.Medium, containerOptions: { fullscreenOnMobile: true } },
      { positionGuid },
    );
  }

  public openRenamePositionDialog(positionGuid: string, positionName: string): Observable<any> {
    return this.dialogService.openCustom(
      RenamePositionDialogComponent,
      { width: DialogWidth.SmallX, containerOptions: { fullscreenOnMobile: true } },
      { positionGuid, positionName },
    ).afterClosed;
  }

  public openDeletePositionDialog(position: TellentPosition): Observable<any> {
    return this.dialogService.openCustom(
      DeletePositionDialogComponent,
      { width: DialogWidth.SmallXX, containerOptions: { fullscreenOnMobile: true } },
      { position },
    ).afterClosed;
  }

  public openAssignMembersDialog(position: TellentPosition): Observable<SelectMembersDialogResult> {
    return this.dialogService.openCustom(
      SelectTeamMembersDialogComponent,
      { width: DialogWidth.SmallX, containerOptions: { fullscreenOnMobile: true } },
      {
        membersIds: position.members ? position.members.map(member => member.guid) : [],
        header: this.t(
          'tlc.positions.position_details_dialog.tabs.employees.assign_members_dialog.header',
        ),
        subheader: position.name,
        ctaButtonText: this.t(
          'tlc.positions.position_details_dialog.tabs.employees.assign_members_dialog.assign',
        ),
      },
    ).afterClosed;
  }

  public openReassignMemberWarningDialog(
    data: ReassignMemberWarningDialogData,
  ): Observable<ReassignMemberWarningDialogResult> {
    return this.dialogService.openCustom(
      ReassignMemberWarningDialogComponent,
      { width: DialogWidth.SmallXX, containerOptions: { fullscreenOnMobile: true } },
      data,
    ).afterClosed;
  }

  public openConfirmPositionChangeDialog(
    data: ConfirmPositionChangeDialogData,
  ): Observable<ConfirmPositionChangeDialogResult> {
    return this.dialogService.openCustom(
      ConfirmPositionChangeDialogComponent,
      { width: DialogWidth.Small, containerOptions: { fullscreenOnMobile: true } },
      data,
    ).afterClosed;
  }

  public openPositionHistoryDialog(
    data: PositionHistoryDialogData,
  ): Observable<PositionHistoryDialogResult> {
    return this.dialogService.openCustom(
      PositionHistoryDialogComponent,
      { width: DialogWidth.Medium, containerOptions: { fullscreenOnMobile: true } },
      data,
    ).afterClosed;
  }

  public openUnassignSkillMappingDialog(
    position: TellentPosition,
    item: SkillMapping,
  ): Observable<boolean> {
    return this.dialogService.error({
      title: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.unassign_error_dialog.title',
      ),
      message: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.unassign_error_dialog.message',
        { skillMappingName: item.name, positionName: position.name },
      ),
      agreeLabel: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.list.skill_mapping.unassign_error_dialog.agree_label',
      ),
      containerOptions: { fullscreenOnMobile: true },
    });
  }

  public openUnassignSkillDialog(
    position: TellentPosition,
    item: AssignedSkill,
  ): Observable<boolean> {
    return this.dialogService.error({
      title: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.unassign_skill_error_dialog.title',
      ),
      message: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.unassign_skill_error_dialog.message',
        { skillName: item.skill.name, positionName: position.name },
      ),
      agreeLabel: this.t(
        'tlc.positions.position_details_dialog.tabs.skills.unassign_skill_error_dialog.agree_label',
      ),
      containerOptions: { fullscreenOnMobile: true },
    });
  }

  public openUnassignMemberDialog(
    positionData: TellentPosition,
    member: TellentPositionMember,
  ): Observable<UnassignMemberFromPositionDialogResult> {
    return this.dialogService.openCustom(
      UnassignMemberFromPositionDialogComponent,
      { width: DialogWidth.SmallXX, containerOptions: { fullscreenOnMobile: true } },
      { positionData, member },
    ).afterClosed;
  }
}
